import {
    AutocompleteInput,
    CreateButton,
    Datagrid,
    ExportButton,
    Filter,
    Link,
    List,
    ReferenceField,
    RichTextField,
    TextField,
    TextInput,
    TopToolbar,
    useGetList,
    useListContext,
    useNotify,
    useRecordContext,
} from 'react-admin';
import { NoDataSection } from '../../common/NoDataSection';
import { DeleteWithDialogAction } from '../../common/DeleteWithDialogAction';
import { useNavigate, useParams } from 'react-router';
import { Add, Description, Edit, PictureAsPdf, Videocam } from '@mui/icons-material';
import { Button, Dialog, DialogContent, Typography } from '@mui/material';
import ThumbnailField from '../../common/ThumbnailField';
import useIsPermission from '../../hooks/useIsPermission';
import FormattedDateTimeField from '../../common/FormattedDateTimeField';
import ConfirmDeleteButton from '../../common/ConfirmDeleteButton';
import ClearFiltersOnRouteChange from '../../common/ClearFiltersOnRouteChange';
import { useState } from 'react';
import { PlayArrow } from '@mui/icons-material';
import VideoPlayerPage from '../../common/VideoPlayer/VideoPlayerPage';

const ShowVideoIcon = (props: {
    open: boolean;
    handleClose: ((event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void) | undefined;
}) => {
    const record = useRecordContext();

    return (
        <Dialog open={props.open} onClose={props.handleClose} maxWidth="lg" fullWidth>
            <DialogContent>
                <VideoPlayerPage url={record.videoUrl} type={'YOUTUBE'} />
            </DialogContent>
        </Dialog>
    );
};

const ListActions = () => {
    const { isWritable } = useIsPermission('DEMO_VIDEOS');
    const { filterValues } = useListContext();

    const query = new URLSearchParams(filterValues).toString();

    return (
        <TopToolbar>
            {isWritable && (
                <>
                    <Link to={`/demo-videos/create?${query}`} style={{ textDecoration: 'none' }}>
                        <Add />
                        <Typography variant="button">Create</Typography>
                    </Link>
                </>
            )}
            <ExportButton />
        </TopToolbar>
    );
};

const EditButton = () => {
    const navigate = useNavigate();
    const record: any = useRecordContext<any>();

    return (
        <Button
            startIcon={<Edit />}
            onClick={e => {
                navigate(`/demo-videos/${record?.id}/edit`);
            }}
        >
            Edit
        </Button>
    );
};
const DemoVideoCategoryAutoComplete = (props: any) => {
    const { data, isLoading } = useGetList(
        'demo-video-categories',
        {
            pagination: { page: 1, perPage: 50 },
            sort: { field: 'created_at', order: 'DESC' },
            filter: { deleted: false },
        },
        {
            refetchOnReconnect: false,
            refetchOnWindowFocus: false,
        },
    );

    return (
        <AutocompleteInput
            {...props}
            source="demoVideoCategoryId"
            label="Category"
            choices={data || []}
            optionText="name"
            optionValue="id"
            loading={isLoading}
        />
    );
};

const ListFilter = (props: any) => (
    <Filter {...props}>
        <TextInput label="Search" source="title" alwaysOn />
        <DemoVideoCategoryAutoComplete alwaysOn />
    </Filter>
);
export const VideoThumbnail = () => {
    const record = useRecordContext();
    const notify = useNotify();
    const [open, setOpen] = useState(false);

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <div className={'flex space-x-2 items-center'}>
            <div>
                <ShowVideoIcon open={open} handleClose={handleClose} />
                <div
                    className={
                        'min-h-[45px] min-w-[100px] aspect-video rounded overflow-hidden bg-gray-100 border bg-center bg-contain bg-no-repeat relative'
                    }
                    style={{ backgroundImage: `url(${record.thumbnail})` }}
                    onClick={handleClickOpen}
                >
                    <div
                        className={
                            'p-2 text-center absolute -bottom-0 font-semibold bg-white px-1.5 py-0.5 rounded-md text-xs items-center flex space-x-0.5'
                        }
                    ></div>
                    <div className={'absolute inset-0 flex items-center justify-center'}>
                        <PlayArrow
                            style={{ fontSize: '48px', color: 'rgba(255, 255, 255, 0.8)' }}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};
VideoThumbnail.defaultProps = {
    label: 'Video',
};
const DemoVideosList = () => {
    const navigate = useNavigate();
    const { isDeletable, isWritable, isUpdatable } = useIsPermission('DEMO_VIDEOS');

    return (
        <List
            sort={{ field: 'created_at', order: 'DESC' }}
            filters={<ListFilter />}
            actions={<ListActions />}
            empty={
                <NoDataSection
                    resource={'demo-videos'}
                    onCreate={() => {
                        navigate(`/demo-videos/create`);
                    }}
                />
            }
            queryOptions={{
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            }}
        >
            <ClearFiltersOnRouteChange enableFilterCheck={true} />

            <Datagrid>
                <VideoThumbnail />
                <TextField source="title" sortable={false} />
                <RichTextField source="description" label="Description" sortable={false} />
                <FormattedDateTimeField source="createdAt" sortable={false} />
                {isUpdatable && <EditButton />}
                {isDeletable && <ConfirmDeleteButton />}
            </Datagrid>
        </List>
    );
};

export default DemoVideosList;
